import type { ComponentType } from 'react';
import {
  Activity,
  BarChart3,
  Bell,
  BookOpen,
  Brain,
  FileText,
  Grid3x3,
  Hash,
  MessageSquare,
  Network,
  Server,
  Swords,
  Target,
  TestTube2,
  Ticket,
  Zap,
} from 'lucide-react';
import { integrationPaths } from './paths';

export interface IntegrationsSearchEntry {
  id: string;
  title: string;
  description: string;
  path: string;
  icon: ComponentType<{ className?: string }>;
  badge?: string;
  keywords: string[];
}

/** Canonical search catalog for Integrations module routes (GlobalSearch + CommandCenter). */
export const INTEGRATIONS_SEARCH_ENTRIES: IntegrationsSearchEntry[] = [
  {
    id: 'integrations-hub',
    title: 'Integrations Hub',
    description: 'Connect and manage third-party integrations',
    path: integrationPaths.hub,
    icon: Zap,
    badge: 'Hub',
    keywords: ['integrations', 'connect', 'plugins', 'extensions', 'providers', 'hub'],
  },
  {
    id: 'notification-center',
    title: 'Notification Center',
    description: 'Integration health, routing overview, and status',
    path: integrationPaths.notificationCenter,
    icon: Bell,
    keywords: ['notification', 'center', 'routing', 'alerts', 'integrations', 'health'],
  },
  {
    id: 'routing-studio',
    title: 'Routing Studio',
    description: 'Visual IF→THEN alert routing flows',
    path: integrationPaths.notificationCenterRouting,
    icon: Network,
    badge: '⚡',
    keywords: ['routing', 'rules', 'flows', 'alerts', 'studio', 'if then', 'escalation'],
  },
  {
    id: 'resource-groups',
    title: 'Resource Groups',
    description: 'Group servers, apps, and databases for routing',
    path: integrationPaths.notificationCenterResources,
    icon: Server,
    keywords: ['resource', 'groups', 'servers', 'apps', 'databases', 'targets'],
  },
  {
    id: 'destination-matrix',
    title: 'Destination Matrix',
    description: 'Map alert severities to notification destinations',
    path: integrationPaths.notificationCenterMatrix,
    icon: Target,
    keywords: ['destination', 'matrix', 'severity', 'mapping', 'channels', 'pagerduty'],
  },
  {
    id: 'communication-hub',
    title: 'Communication Hub',
    description: 'Slack, Teams, Discord, and email integrations',
    path: integrationPaths.notificationCenterComms,
    icon: MessageSquare,
    keywords: ['communication', 'slack', 'teams', 'discord', 'email', 'chat', 'webhook'],
  },
  {
    id: 'ticketing-hub',
    title: 'Ticketing Hub',
    description: 'Jira, Linear, Asana, and ServiceNow routing',
    path: integrationPaths.notificationCenterTickets,
    icon: Ticket,
    keywords: ['ticketing', 'jira', 'linear', 'asana', 'servicenow', 'issues', 'tickets'],
  },
  {
    id: 'routing-ai-assistant',
    title: 'Routing AI Assistant',
    description: 'AI help for alert routing and integration setup',
    path: integrationPaths.notificationCenterAi,
    icon: Brain,
    badge: 'AI',
    keywords: ['ai', 'assistant', 'routing', 'help', 'suggestions', 'automation'],
  },
  {
    id: 'integration-war-room',
    title: 'Integration War Room',
    description: 'Live integration incident coordination',
    path: integrationPaths.notificationCenterWarRoom,
    icon: Swords,
    keywords: ['war room', 'incident', 'integration', 'outage', 'coordination'],
  },
  {
    id: 'integration-testing-center',
    title: 'Testing Center',
    description: 'Test alert delivery across integrations',
    path: integrationPaths.notificationCenterTesting,
    icon: TestTube2,
    keywords: ['testing', 'test', 'delivery', 'simulate', 'dry run', 'notifications'],
  },
  {
    id: 'slack-overview',
    title: 'Slack Integration',
    description: 'Slack connection status and overview',
    path: integrationPaths.slackOverview,
    icon: Grid3x3,
    keywords: ['slack', 'overview', 'workspace', 'integration'],
  },
  {
    id: 'slack-workspaces',
    title: 'Slack Workspaces',
    description: 'Manage connected Slack workspaces',
    path: integrationPaths.slackWorkspaces,
    icon: MessageSquare,
    keywords: ['slack', 'workspaces', 'workspace', 'oauth', 'bot'],
  },
  {
    id: 'slack-channels',
    title: 'Slack Channels',
    description: 'Browse and configure Slack channels',
    path: integrationPaths.slackChannels,
    icon: Hash,
    keywords: ['slack', 'channels', 'hash', 'routing', 'alerts'],
  },
  {
    id: 'slack-routing-studio',
    title: 'Slack Alert Routing',
    description: 'Route alerts to Slack channels',
    path: integrationPaths.slackRouting,
    icon: Network,
    keywords: ['slack', 'routing', 'alerts', 'channels', 'rules'],
  },
  {
    id: 'slack-alert-rules',
    title: 'Slack Alert Rules',
    description: 'Configure Slack-specific alert rules',
    path: integrationPaths.slackAlertRules,
    icon: Target,
    keywords: ['slack', 'alert', 'rules', 'conditions', 'filters'],
  },
  {
    id: 'slack-templates',
    title: 'Slack Message Templates',
    description: 'Customize Slack notification message formats',
    path: integrationPaths.slackTemplates,
    icon: FileText,
    keywords: ['slack', 'templates', 'messages', 'formatting', 'blocks'],
  },
  {
    id: 'slack-resource-groups',
    title: 'Slack Resource Groups',
    description: 'Resource groups scoped to Slack routing',
    path: integrationPaths.slackResourceGroups,
    icon: Server,
    keywords: ['slack', 'resource', 'groups', 'servers', 'apps'],
  },
  {
    id: 'slack-delivery-logs',
    title: 'Slack Delivery Logs',
    description: 'Slack message delivery history and errors',
    path: integrationPaths.slackLogs,
    icon: Activity,
    keywords: ['slack', 'logs', 'delivery', 'errors', 'history', 'audit'],
  },
  {
    id: 'slack-analytics',
    title: 'Slack Analytics',
    description: 'Slack notification volume and performance metrics',
    path: integrationPaths.slackAnalytics,
    icon: BarChart3,
    keywords: ['slack', 'analytics', 'metrics', 'volume', 'delivery rate'],
  },
  {
    id: 'slack-testing',
    title: 'Slack Testing',
    description: 'Send test alerts to Slack channels',
    path: integrationPaths.slackTesting,
    icon: TestTube2,
    keywords: ['slack', 'testing', 'test', 'simulate', 'dry run'],
  },
  {
    id: 'slack-integration-help',
    title: 'Slack Setup Guide',
    description: 'Step-by-step Slack integration setup',
    path: integrationPaths.slackHelp,
    icon: BookOpen,
    keywords: ['slack', 'help', 'setup', 'guide', 'oauth', 'scopes', 'documentation'],
  },
];
